import { Table, Button, Card, Loader, Center, Modal, Stack, Select, NumberInput, Group, Text, SimpleGrid, ThemeIcon, ActionIcon, Tooltip, SegmentedControl, Badge, Textarea, Alert } from '@mantine/core';
import { DateTimePicker, MonthPickerInput } from '@mantine/dates';
import { AreaChart } from '@mantine/charts';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconCash, IconReceipt, IconChartLine, IconInfoCircle, IconPencil } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { useRevenue, useClasses } from '../hooks/useLookups';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { ServerSearchSelect } from '../components/ServerSearchSelect';
import { PageHeader } from '../components/PageHeader';
import { PaymentReceiptModal } from '../components/PaymentReceipt';
import { BalancesPanel } from '../components/BalancesPanel';
import { TuitionPlansPanel } from '../components/TuitionPlansPanel';
import { CashbookPanel } from '../components/CashbookPanel';
import { SectionCard } from '../components/SectionCard';
import { formatSom, formatDate, formatSomShort } from '../lib/format';
import { monthLabel, monthToDate, normalizeMonth, recentYears, thisMonth, ym } from '../lib/month';

interface Payment {
  id: string;
  amount: number;
  paidAt: string;
  forMonth?: string;
  method: string;
  note?: string | null;
  /** To'ldirilgan bo'lsa — bu qatorning o'zi STORNO (manfiy summa). */
  reversalOfId?: string | null;
  /** Bu to'lovni bekor qilgan storno qatori (bo'lsa) — to'lov endi haqiqiy emas. */
  reversal?: { id: string; reversalReason?: string | null } | null;
  student?: { id: string; fullName: string };
}

/** Tushum grafigi uchun yillar — Hisobotlar sahifasi bilan AYNI ro'yxat (`lib/month.ts`). */
const YEAR_OPTIONS = recentYears();

/** "2026-11" + 2 → "2027-01" (backend `oyQosh` bilan bir xil). */
function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return ym(d);
}

/**
 * Sana-vaqt filtri serverga MINTAQASIZ ("2026-08-02T14:30") yuboriladi — backend uni
 * Toshkent devor-soati deb talqin qiladi. `toISOString()` bo'lsa brauzer mintaqasiga
 * qarab oraliq siljib ketardi (Railway serveri UTC, maktab esa UTC+5).
 */
const wallClock = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DDTHH:mm:ss') : undefined);

/**
 * "Gacha" chegarasi: kalendardan YANGI KUN tanlanganda vaqt 00:00 bo'lib qolsa, o'sha kun
 * oraliqdan butunlay tushib qolardi — shuning uchun kun oxiriga (23:59:59) o'tkaziladi.
 * Vaqt qo'lda kiritilsa (kun o'zgarmaydi) tegilmaydi — soatli filtr ishlayveradi.
 */
function endBound(next: Date | null, prev: Date | null): Date | null {
  if (!next || next.getHours() || next.getMinutes() || dayjs(next).isSame(prev, 'day')) return next;
  return dayjs(next).hour(23).minute(59).second(59).toDate();
}

export default function Payments() {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const METHODS = [
    { value: 'CASH', label: t('paymentsPage.methodCash') },
    { value: 'CARD', label: t('paymentsPage.methodCard') },
    { value: 'TRANSFER', label: t('paymentsPage.methodTransfer') },
    { value: 'OTHER', label: t('paymentsPage.methodOther') },
  ];
  // "Qaysi oy uchun" filtri — `MonthPickerInput` (Balanslar/Hisobotlar bilan AYNI naqsh).
  // AVVAL bu `recentMonths(13)` bilan yasalgan Select edi: kelasi oy uchun oldindan
  // to'langan yozuvni ham, 12 oydan eski oyni ham filtrlab bo'lmasdi (#65).
  // T9: serverga yuboriladigan qiymat DOIM "YYYY-MM" (`lib/month.ts`).
  // To'lov formasidagi oy: kelasi oy (oldindan to'lov) + 18 oy orqaga.
  const payMonthOptions = Array.from({ length: 19 }, (_, i) => {
    const d = new Date();
    const m = ym(new Date(d.getFullYear(), d.getMonth() + 1 - i, 1));
    return { value: m, label: monthLabel(m, monthNames) };
  });
  const qc = useQueryClient();
  // To'lovlar / balanslar (T10) — moliya sahifasining ikki ko'rinishi
  // Sukut — kassa hisoboti (maktab rahbariyati sahifani shu ko'rinish uchun ochadi)
  const [view, setView] = useState('cashbook');
  const [opened, { open, close }] = useDisclosure(false);
  const { data: classes } = useClasses();

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [methodF, setMethodF] = useState<string>('');
  const [classF, setClassF] = useState<string>('');
  const [forMonthF, setForMonthF] = useState<string>('');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [sort, setSort] = useState('paidAt:desc');
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Payment | null>(null);
  // "Oylik tushum" grafigi yili — avval joriy yilga QATTIQ bog'langan edi,
  // shu sababli o'tgan yilgi tushumni bu sahifada ko'rib bo'lmasdi (#65).
  const [year, setYear] = useState(YEAR_OPTIONS[0]);

  // Ro'yxat va yuqoridagi kartochkalar AYNI filtrlarni ishlatadi — aks holda
  // oraliq tanlanganda "Umumiy tushum" ro'yxatga mos kelmay qolardi.
  // (`sort` bu obyektga KIRMAYDI — summary uchun saralash ahamiyatsiz.)
  const filters = {
    q: qDeb || undefined,
    method: methodF || undefined,
    classId: classF || undefined,
    forMonth: forMonthF || undefined,
    from: wallClock(from),
    to: wallClock(to),
    // Qaytarilgan pulning MANFIY qatori ro'yxatda ko'rsatilmaydi: asl to'lov o'zi
    // "Qaytarilgan" belgisi bilan (ustidan chizilgan) turadi — ikki qator chalg'itardi.
    hideReversals: '1',
  };
  const anyFilter = !!(qDeb || methodF || classF || forMonthF || from || to);
  const isPayments = view === 'payments';
  // Balanslar ko'rinishida to'lovlar ro'yxati/xulosasi so'ralmaydi (ortiqcha so'rov yo'q).
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    ...filters, sort,
  };

  const { items: payments, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Payment>('payments', '/payments', filtrlar, 20, isPayments);
  const { data: summary } = useQuery({
    queryKey: ['payments-summary', filters],
    queryFn: async () => (await api.get<{ total: number; count: number }>('/payments/summary', { params: filters })).data,
    enabled: isPayments,
  });

  const { data: revenue } = useRevenue(year, isPayments);

  /*
   * `forMonth` MAJBURIY va standarti — JORIY OY.
   * NEGA: oysiz ("forMonth: null") to'lov qarzni KAMAYTIRMAYDI — `backend/src/lib/balance.ts`
   * hisobga faqat oyi bor to'lovlarni oladi. Ya'ni kassir oyni tanlamay 1 000 000 so'm qabul
   * qilsa, o'quvchi to'liq qarzdor bo'lib qolardi va pul oylik tushumga kirmasdi — bu jimgina
   * pul yo'qotish edi. Endi maydon bo'sh qololmaydi: standart qiymat bor, `validate` tekshiradi,
   * Select'da `required` va `clearable` YO'Q. Backend ham `POST /payments` da oyni talab qiladi.
   */
  const form = useForm({
    initialValues: { studentId: '', amount: 0, method: 'CASH', forMonth: thisMonth(), monthCount: '1', note: '' },
    validate: {
      studentId: (v) => (!v ? t('paymentsPage.selectStudent') : null),
      amount: (v) => (v <= 0 ? t('expenses.amountRequired') : null),
      forMonth: (v) => (!normalizeMonth(v) ? t('paymentsPage.forMonthRequired') : null),
    },
  });

  const createMut = useMutation({
    // Formani XOM yuborish o'rniga aniq payload (Expenses.tsx dagi naqsh bilan bir xil):
    // oy DOIM normallashgan "YYYY-MM" bo'lib ketadi — backend `normalizeMonth` bilan mos.
    mutationFn: (v: typeof form.values) => api.post<{ id: string }>('/payments', {
      studentId: v.studentId,
      amount: v.amount,
      method: v.method,
      forMonth: normalizeMonth(v.forMonth),
      monthCount: Number(v.monthCount) || 1,
      note: v.note.trim() || undefined,
    }),
    onSuccess: (res) => {
      notifications.show({ color: 'green', message: t('paymentsPage.paymentAccepted') });
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['payments-summary'] });
      // To'lov tushumni va qarzni o'zgartiradi — shu sahifadagi revenue grafigi, Dashboard va
      // Hisobotlar qarzdorlar jadvali ham yangilansin (avval eskirgan holatда qolardi).
      qc.invalidateQueries({ queryKey: ['revenue'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['rep-debtors'] });
      // T10: balans holati ham to'lovga bog'liq — "Balanslar" ko'rinishi eskirmasin
      qc.invalidateQueries({ queryKey: ['balances'] });
      form.reset();
      // `reset()` boshlang'ich qiymatga qaytaradi — u ilk renderda hisoblangan.
      // Uzoq ochiq sessiyada oy almashsa eskisi qolib ketmasin (Expenses'dagi naqsh).
      form.setFieldValue('forMonth', thisMonth());
      close();
      // To'lov qabul qilindi — darhol chek ochiladi (chop etish uchun tayyor)
      if (res.data?.id) setReceiptId(res.data.id);
    },
  });

  return (
    <>
      <PageHeader title={t('pages.payments.title')} action={<Button leftSection={<IconPlus size={18} />} onClick={open}>{t('dashboard.qPayment')}</Button>} />

      {/* To'lovlar / balanslar (T10) */}
      <SegmentedControl
        value={view}
        onChange={setView}
        mb="md"
        data={[
          { value: 'cashbook', label: t('cashbook.tab') },
          { value: 'payments', label: t('balances.tabPayments') },
          { value: 'balances', label: t('balances.tab') },
          { value: 'plans', label: t('plans.tab') },
        ]}
      />

      {view === 'cashbook' ? <CashbookPanel onShowDebtors={() => setView('balances')} /> : view === 'plans' ? <TuitionPlansPanel /> : !isPayments ? <BalancesPanel /> : (
      <>
      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
        <Card>
          <Group>
            <ThemeIcon size={48} radius="md" variant="light" color="green"><IconCash size={26} /></ThemeIcon>
            <Stack gap={0}>
              <Text size="xl" fw={700}>{formatSom(summary?.total)}</Text>
              <Text size="sm" c="dimmed">{t('paymentsPage.totalRevenue')}</Text>
            </Stack>
          </Group>
        </Card>
        <Card>
          <Stack gap={0} justify="center" h="100%">
            <Text size="xl" fw={700}>{summary?.count ?? 0}</Text>
            <Text size="sm" c="dimmed">{t('paymentsPage.paymentsCount')}</Text>
          </Stack>
        </Card>
      </SimpleGrid>

      <SectionCard
        title={t('reports.monthlyRevenue', { year })}
        icon={IconChartLine}
        color="green"
        mb="md"
        right={
          <Select
            aria-label={t('reports.year')}
            data={YEAR_OPTIONS}
            value={year}
            onChange={(v) => v && setYear(v)}
            allowDeselect={false}
            size="xs"
            w={100}
          />
        }
      >
        {revenue?.length ? (
          <AreaChart
            h={260}
            data={revenue.map((r) => ({ oy: monthNames[Number(r.month.slice(5)) - 1] ?? r.month.slice(5), value: r.total, cash: r.cash }))}
            dataKey="oy"
            withLegend
            series={[
              { name: 'value', color: 'green.6', label: t('charts.monthIncome') },
              { name: 'cash', color: 'blue.5', label: t('charts.cashIncome') },
            ]}
            curveType="natural"
            withGradient
            withDots={false}
            valueFormatter={(v) => formatSom(v)}
            yAxisProps={{ width: 58, tickFormatter: (v: number) => formatSomShort(v) }}
          />
        ) : (
          <Text c="dimmed" size="sm">{t('reports.noData')}</Text>
        )}
        {!!revenue?.length && <Text fz="xs" c="dimmed" mt="xs">{t('reports.revenueHint')}</Text>}
      </SectionCard>

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('paymentsPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'paidAt:desc', label: t('att.sortDateDesc') },
          { value: 'paidAt:asc', label: t('att.sortDateAsc') },
          { value: 'amount:desc', label: t('paymentsPage.sortAmountDesc') },
          { value: 'amount:asc', label: t('paymentsPage.sortAmountAsc') },
        ]}
      >
        {/* METHODS ro'yxati jadval yorliqlari bilan bitta manba — 'OTHER' ham filtrlanadi */}
        <Select
          placeholder={t('paymentsPage.method')}
          data={METHODS}
          value={methodF || null}
          onChange={(v) => { setMethodF(v ?? ''); setPage(1); }}
          clearable w={130}
        />
        <Select
          placeholder={t('fields.class')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classF || null}
          onChange={(v) => { setClassF(v ?? ''); setPage(1); }}
          clearable searchable w={140}
        />
        {/* Istalgan yil+oy: kelasi oy (oldindan to'lov) ham, 12 oydan eski oy ham tanlanadi */}
        <MonthPickerInput
          label={t('expenses.forMonth')}
          placeholder={t('common.selectPlaceholder')}
          value={monthToDate(forMonthF)}
          onChange={(d) => { setForMonthF(d ? ym(d) : ''); setPage(1); }}
          valueFormat="YYYY MMMM"
          clearable
          w={175}
        />
        {/* Sana oralig'i SOAT-DAQIQA bilan — kiritilgan vaqt Toshkent devor-soati */}
        <DateTimePicker
          label={t('paymentsPage.dateFrom')}
          placeholder={t('paymentsPage.dateTimePlaceholder')}
          valueFormat="DD.MM.YYYY HH:mm"
          value={from}
          onChange={(v) => { setFrom(v); setPage(1); }}
          clearable
          w={185}
        />
        <DateTimePicker
          label={t('paymentsPage.dateTo')}
          placeholder={t('paymentsPage.dateTimePlaceholder')}
          valueFormat="DD.MM.YYYY HH:mm"
          value={to}
          onChange={(v) => { setTo(endBound(v, to)); setPage(1); }}
          clearable
          w={185}
        />
        <TableExport<Payment>
          endpoint="/payments"
          params={filtrlar}
          filename={t('nav.payments')}
          ustunlar={[
            { key: 'student', label: t('att.student'), get: (r) => r.student?.fullName ?? '' },
            { key: 'amount', label: t('fields.amount') },
            { key: 'method', label: t('paymentsPage.method') },
            { key: 'forMonth', label: t('fields.month') },
            { key: 'paidAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.paidAt}
        />
      </TableToolbar>

      {/* "Qaysi oy uchun" + to'lov sanasi oralig'i BIRGA qo'llanadi (VA mantiqi):
          ular turli narsani anglatgani uchun natija bo'sh chiqishi mumkin —
          buni aytmasak "to'lovlar yo'qoldi" degan xulosa chiqardi (#65).
          Xarajatlar sahifasidagi izoh naqshi bilan bir xil. */}
      {forMonthF && (from || to) && (
        <Group gap="xs" mb="xs" align="center">
          <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
          <Text fz="xs" c="dimmed" style={{ flex: '1 1 240px', minWidth: 0 }}>{t('paymentsPage.forMonthRangeHint')}</Text>
          <Button variant="subtle" size="compact-xs" onClick={() => { setFrom(null); setTo(null); setPage(1); }}>
            {t('paymentsPage.clearRange')}
          </Button>
        </Group>
      )}

      <SectionCard title={t('common.list')} icon={IconCash} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !payments.length ? (
          <Center h={140}><Text c="dimmed">{anyFilter ? t('paymentsPage.noMatch') : t('studentDetailPage.noPayments')}</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('att.student')}</Table.Th>
                  <Table.Th>{t('fields.amount')}</Table.Th>
                  <Table.Th>{t('paymentsPage.method')}</Table.Th>
                  <Table.Th>{t('fields.month')}</Table.Th>
                  <Table.Th>{t('fields.date')}</Table.Th>
                  <Table.Th w={100} ta="right">{t('common.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {payments.map((p) => {
                  const isStorno = !!p.reversalOfId;
                  const isReversed = !!p.reversal;
                  return (
                  <Table.Tr key={p.id}>
                    <Table.Td fw={500}>{p.student?.fullName ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text fw={600} c={isStorno ? 'red.7' : isReversed ? 'dimmed' : 'green.7'} td={isReversed ? 'line-through' : undefined}>
                          {formatSom(p.amount)}
                        </Text>
                        {isStorno && <Badge size="xs" color="red" variant="light">{t('paymentsPage.badgeStorno')}</Badge>}
                        {isReversed && (
                          <Tooltip label={p.reversal?.reversalReason ?? ''} disabled={!p.reversal?.reversalReason} withArrow>
                            <Badge size="sm" color="orange" variant="light">{t('paymentsPage.badgeReversed')}</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{METHODS.find((m) => m.value === p.method)?.label ?? p.method}</Table.Td>
                    {/* T9: bazada "2026-5" ham, "2026-07" ham uchraydi — ko'rsatishdan
                        oldin normallashtiriladi, ustun bir xil ko'rinadi */}
                    <Table.Td>{p.forMonth ? monthLabel(p.forMonth, monthNames) : '—'}</Table.Td>
                    <Table.Td>{formatDate(p.paidAt)}</Table.Td>
                    <Table.Td ta="right">
                      <Group gap={6} justify="flex-end" wrap="nowrap">
                        {/* Qaytarilgan pul qatori va qaytarilgan to'lov tahrirlanmaydi (backend ham rad etadi) */}
                        {!isStorno && !isReversed && (
                          <Tooltip label={t('paymentsPage.edit')} withArrow>
                            <ActionIcon variant="light" color="blue" onClick={() => setEditing(p)} aria-label={t('paymentsPage.edit')}>
                              <IconPencil size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {/* Eski to'lov chekini qayta ochib chop etish */}
                        <Tooltip label={t('receipt.open')} withArrow>
                          <ActionIcon variant="light" onClick={() => setReceiptId(p.id)} aria-label={t('receipt.open')}>
                            <IconReceipt size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />
      </>
      )}

      <Modal opened={opened} onClose={close} title={t('dashboard.qPayment')} centered>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <ServerSearchSelect
              label={t('att.student')}
              placeholder={t('paymentsPage.studentSearchPlaceholder')}
              required
              endpoint="/students"
              mapOption={(st: { id: string; fullName: string; class?: { name: string } | null }) => ({ value: st.id, label: `${st.fullName}${st.class ? ` · ${st.class.name}` : ''}` })}
              value={form.values.studentId || null}
              onChange={(v) => form.setFieldValue('studentId', v || '')}
              error={form.errors.studentId}
            />
            <NumberInput label={t('paymentsPage.amountSom')} thousandSeparator=" " min={0} required {...form.getInputProps('amount')} />
            <Select label={t('paymentsPage.paymentMethod')} data={METHODS} {...form.getInputProps('method')} />
            {/* T9: qiymat DOIM "YYYY-MM" (backend `normalizeMonth` bilan mos),
                ro'yxat esa kelasi oydan 18 oy orqagacha — yil qattiq yozilmaydi. */}
            <Select
              label={t('expenses.forMonth')}
              placeholder={t('common.selectPlaceholder')}
              data={payMonthOptions}
              required
              allowDeselect={false}
              searchable
              {...form.getInputProps('forMonth')}
            />
            {/* Oldindan to'lov: summa oylarga teng bo'linib, har oy uchun alohida
                yoziladi (qarz va oy hisoboti to'g'ri chiqadi), chek esa BITTA. */}
            <Select
              label={t('paymentsPage.monthCount')}
              data={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: t('paymentsPage.monthsN', { count: i + 1 }) }))}
              allowDeselect={false}
              {...form.getInputProps('monthCount')}
            />
            {Number(form.values.monthCount) > 1 && normalizeMonth(form.values.forMonth) && (
              <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />} py="xs">
                {t('paymentsPage.splitPreview', {
                  from: monthLabel(form.values.forMonth, monthNames),
                  to: monthLabel(addMonths(normalizeMonth(form.values.forMonth)!, Number(form.values.monthCount) - 1), monthNames),
                  each: formatSom(Math.floor((form.values.amount || 0) / Number(form.values.monthCount))),
                })}
              </Alert>
            )}
            <Textarea label={t('paymentsPage.note')} placeholder={t('paymentsPage.notePlaceholder')} autosize minRows={1} maxRows={4} {...form.getInputProps('note')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <PaymentReceiptModal paymentId={receiptId} onClose={() => setReceiptId(null)} />
      <PaymentEditModal
        payment={editing}
        methods={METHODS}
        monthOptions={payMonthOptions}
        onClose={() => setEditing(null)}
        onSaved={(newId) => {
          setEditing(null);
          ['payments', 'payments-summary', 'revenue', 'dashboard', 'rep-debtors', 'balances'].forEach((k) =>
            qc.invalidateQueries({ queryKey: [k] })
          );
          // Moliyaviy tahrirda YANGI to'lov yaratiladi — uning chekini darhol ochamiz.
          if (newId) setReceiptId(newId);
        }}
      />
    </>
  );
}

/**
 * TO'LOVNI TAHRIRLASH oynasi.
 *
 * Backend (`PATCH /payments/:id`) ikki yo'l bilan ishlaydi: faqat izoh o'zgarsa —
 * joyida yangilanadi; summa/oy/usul/sana o'zgarsa — asl to'lov STORNO qilinib,
 * to'g'rilangan YANGI to'lov yoziladi. Shuning uchun moliyaviy maydon o'zgargandagina
 * "sabab" maydoni chiqadi va majburiy bo'ladi, foydalanuvchiga esa nima bo'lishi
 * oldindan aytiladi — eski chek bekor bo'lib, yangi chek beriladi.
 *
 * Sana FAQAT o'zgartirilganda yuboriladi: aks holda daqiqagacha yaxlitlangan qiymat
 * asl soniyali vaqtdan farq qilib, oddiy izoh tahriri ham storno bo'lib ketardi.
 */
function PaymentEditModal({ payment, methods, monthOptions, onClose, onSaved }: {
  payment: Payment | null;
  methods: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: (newPaymentId: string | null) => void;
}) {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  return (
    <Modal opened={!!payment} onClose={onClose} title={t('paymentsPage.editTitle')} centered>
      {payment && (
        <PaymentEditForm key={payment.id} payment={payment} methods={methods} onClose={onClose} onSaved={onSaved}
          monthOptions={
            payment.forMonth && !monthOptions.some((o) => o.value === normalizeMonth(payment.forMonth))
              ? [{ value: normalizeMonth(payment.forMonth)!, label: monthLabel(payment.forMonth, monthNames) }, ...monthOptions]
              : monthOptions
          }
        />
      )}
    </Modal>
  );
}

function PaymentEditForm({ payment, methods, monthOptions, onClose, onSaved }: {
  payment: Payment;
  methods: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: (newPaymentId: string | null) => void;
}) {
  const { t } = useTranslation();
  const asl = {
    amount: payment.amount,
    method: payment.method,
    forMonth: normalizeMonth(payment.forMonth) ?? '',
    paidAt: new Date(payment.paidAt),
    note: payment.note ?? '',
  };
  const form = useForm({
    initialValues: { ...asl, reason: '' },
    validate: {
      amount: (v) => (v <= 0 ? t('expenses.amountRequired') : null),
      forMonth: (v) => (!normalizeMonth(v) ? t('paymentsPage.forMonthRequired') : null),
    },
  });
  const v = form.values;
  const sanaOzgardi = !!v.paidAt && Math.abs(v.paidAt.getTime() - asl.paidAt.getTime()) >= 60_000;
  const moliyaviy = v.amount !== asl.amount || v.method !== asl.method || v.forMonth !== asl.forMonth || sanaOzgardi;
  const izohOzgardi = v.note.trim() !== asl.note.trim();

  const mut = useMutation({
    mutationFn: () => api.patch<{ payment: { id: string }; reversal: { id: string } | null }>(`/payments/${payment.id}`, {
      ...(v.amount !== asl.amount ? { amount: v.amount } : {}),
      ...(v.method !== asl.method ? { method: v.method } : {}),
      ...(v.forMonth !== asl.forMonth ? { forMonth: normalizeMonth(v.forMonth) } : {}),
      ...(sanaOzgardi ? { paidAt: wallClock(v.paidAt) } : {}),
      ...(izohOzgardi ? { note: v.note } : {}),
      ...(moliyaviy ? { reason: v.reason.trim() } : {}),
    }),
    onSuccess: (res) => {
      notifications.show({ color: 'green', message: t('paymentsPage.editSaved') });
      onSaved(res.data.reversal ? res.data.payment.id : null);
    },
  });

  return (
    <form onSubmit={form.onSubmit(() => {
      if (moliyaviy && !v.reason.trim()) { form.setFieldError('reason', t('paymentsPage.editReasonRequired')); return; }
      mut.mutate();
    })}>
      <Stack>
        <Text size="sm" c="dimmed">{payment.student?.fullName ?? '—'}</Text>
        <NumberInput label={t('paymentsPage.amountSom')} thousandSeparator=" " min={0} required {...form.getInputProps('amount')} />
        <Select label={t('paymentsPage.paymentMethod')} data={methods} allowDeselect={false} {...form.getInputProps('method')} />
        <Select label={t('expenses.forMonth')} data={monthOptions} required allowDeselect={false} searchable {...form.getInputProps('forMonth')} />
        <DateTimePicker label={t('fields.date')} valueFormat="DD.MM.YYYY HH:mm" {...form.getInputProps('paidAt')} />
        <Textarea label={t('paymentsPage.note')} autosize minRows={1} maxRows={4} {...form.getInputProps('note')} />
        {moliyaviy && (
          <>
            <Alert color="orange" variant="light" icon={<IconInfoCircle size={18} />}>
              {t('paymentsPage.editFinancialHint')}
            </Alert>
            <Textarea label={t('paymentsPage.editReason')} placeholder={t('paymentsPage.editReasonPlaceholder')} required autosize minRows={2} {...form.getInputProps('reason')} />
          </>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" loading={mut.isPending} disabled={!moliyaviy && !izohOzgardi}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </form>
  );
}
